type PlayerOptions = {
  onLevel: (level: number) => void;
  onBands?: (bands: VoiceBands) => void;
};

export type VoiceBands = [number, number, number, number, number];

const VOICE_BAND_EDGES_HZ = [85, 250, 500, 1_000, 2_000, 4_000] as const;
const VOICE_BAND_GAINS = [1, 1.05, 1.12, 1.3, 1.55] as const;

export function voiceBandsFromSpectrum(
  spectrum: Uint8Array,
  sampleRate: number,
  fftSize: number,
): VoiceBands {
  const binWidthHz = sampleRate / fftSize;
  return VOICE_BAND_GAINS.map((gain, index) => {
    const start = Math.max(1, Math.ceil(VOICE_BAND_EDGES_HZ[index] / binWidthHz));
    const end = Math.min(spectrum.length, Math.ceil(VOICE_BAND_EDGES_HZ[index + 1] / binWidthHz));
    if (end <= start) return 0;
    let energy = 0;
    for (let bin = start; bin < end; bin += 1) energy += spectrum[bin] * spectrum[bin];
    const rms = Math.sqrt(energy / (end - start));
    return Math.max(0, Math.min(1, ((rms - 10) / 90) * gain));
  }) as VoiceBands;
}

export class PcmStreamPlayer {
  private node: AudioWorkletNode | null = null;
  private analyser: AnalyserNode | null = null;
  private gain: GainNode | null = null;
  private fetchController: AbortController | null = null;
  private animationFrame: number | null = null;
  private drainResolve: (() => void) | null = null;

  constructor(
    private readonly context: AudioContext,
    private readonly options: PlayerOptions,
  ) {}

  async initialize() {
    if (this.node) return;
    await this.context.audioWorklet.addModule('/pcm-player-worklet.js');
    this.node = new AudioWorkletNode(this.context, 'mara-pcm-stream', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.smoothingTimeConstant = 0.72;
    this.gain = this.context.createGain();
    this.gain.gain.value = 1;
    this.node.connect(this.analyser).connect(this.gain).connect(this.context.destination);
    this.node.port.onmessage = (event: MessageEvent<{ type?: string }>) => {
      if (event.data.type === 'drained') {
        this.drainResolve?.();
        this.drainResolve = null;
      }
    };
  }

  async play(url: string, externalSignal?: AbortSignal): Promise<void> {
    if (!this.node || !this.analyser) throw new Error('Audio playback is not initialized.');
    this.stop();
    if (this.context.state !== 'running') await this.context.resume();
    const controller = new AbortController();
    this.fetchController = controller;
    const abortFromOutside = () => controller.abort();
    externalSignal?.addEventListener('abort', abortFromOutside, { once: true });
    this.node.port.postMessage({ type: 'reset' });
    this.startMeter();

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok || !response.body) throw new Error('Mara could not find her voice.');
      const sourceRate = Number(response.headers.get('X-Sample-Rate') ?? 24_000);
      this.node.port.postMessage({ type: 'configure', sourceRate });
      const reader = response.body.getReader();
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = value.slice().buffer;
          this.node.port.postMessage({ type: 'chunk', chunk }, [chunk]);
        }
      } finally {
        reader.releaseLock();
      }
      const drained = new Promise<void>((resolve) => {
        this.drainResolve = resolve;
      });
      this.node.port.postMessage({ type: 'end' });
      await drained;
    } finally {
      externalSignal?.removeEventListener('abort', abortFromOutside);
      if (this.fetchController === controller) this.fetchController = null;
      this.stopMeter();
    }
  }

  stop() {
    this.fetchController?.abort();
    this.fetchController = null;
    this.node?.port.postMessage({ type: 'reset' });
    this.drainResolve?.();
    this.drainResolve = null;
    this.stopMeter();
  }

  destroy() {
    this.stop();
    this.node?.disconnect();
    this.analyser?.disconnect();
    this.gain?.disconnect();
    this.node = null;
    this.analyser = null;
    this.gain = null;
  }

  private startMeter() {
    if (!this.analyser) return;
    const timeValues = new Float32Array(this.analyser.fftSize);
    const frequencyValues = new Uint8Array(this.analyser.frequencyBinCount);
    const update = () => {
      if (!this.analyser) return;
      this.analyser.getFloatTimeDomainData(timeValues);
      this.analyser.getByteFrequencyData(frequencyValues);
      let energy = 0;
      for (const value of timeValues) energy += value * value;
      const rms = Math.sqrt(energy / timeValues.length);
      const normalized = Math.max(0, Math.min(1, (rms - 0.006) * 14));
      this.options.onLevel(normalized);
      this.options.onBands?.(voiceBandsFromSpectrum(
        frequencyValues,
        this.context.sampleRate || 48_000,
        this.analyser.fftSize,
      ));
      this.animationFrame = window.requestAnimationFrame(update);
    };
    update();
  }

  private stopMeter() {
    if (this.animationFrame !== null) window.cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
    this.options.onLevel(0);
    this.options.onBands?.([0, 0, 0, 0, 0]);
  }
}
